/**
 * RETAILY rail — minimal horizontal scroll rail with prev/next arrows.
 *
 * A plain custom element (no @theme/component dependency): the only behavior
 * is delegated arrow clicks scrolling the snap track, so listeners on the host
 * survive Shopify section morphing of the children.
 */
class RetailyRail extends HTMLElement {
  connectedCallback() {
    if (this.__retailyBound) return;
    this.__retailyBound = true;

    this.addEventListener('click', (event) => {
      const button = event.target.closest('[data-rail-dir]');
      if (!button) return;
      const track = this.querySelector('.retaily-rail__track');
      if (!track || track.children.length === 0) return;

      const direction = button.dataset.railDir === 'next' ? 1 : -1;
      // Scroll by whole cards and clamp the target: with scroll-snap-type
      // mandatory, Chrome cancels smooth scrolls whose target overshoots the
      // max scroll position (it snaps back to the starting card).
      const step =
        track.children.length > 1
          ? track.children[1].offsetLeft - track.children[0].offsetLeft
          : track.clientWidth;
      const visibleCards = Math.max(1, Math.floor(track.clientWidth / step));
      const maxScroll = track.scrollWidth - track.clientWidth;
      const target = Math.max(0, Math.min(track.scrollLeft + direction * visibleCards * step, maxScroll));
      track.scrollTo({ left: target, behavior: 'smooth' });
    });

    // Direction-aware arrows: hide prev at the start, next at the end.
    // Scroll events don't bubble but CAN be captured on the host, so this
    // survives section morphing replacing the track element.
    const update = () => {
      const track = this.querySelector('.retaily-rail__track');
      if (!track) return;
      const maxScroll = track.scrollWidth - track.clientWidth;
      this.classList.toggle('retaily-rail--at-start', track.scrollLeft <= 1);
      this.classList.toggle('retaily-rail--at-end', track.scrollLeft >= maxScroll - 1);
    };
    this.addEventListener('scroll', update, true);
    // ResizeObserver fires once on observe, which also sets the initial state.
    new ResizeObserver(update).observe(this);
  }
}

if (!customElements.get('retaily-rail')) {
  customElements.define('retaily-rail', RetailyRail);
}
